import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";
import { trackAppEvent, isStandalone } from "../lib/pwaAnalytics";

const DISMISS_KEY = "miltroo_pwa_banner_until";
const SNOOZE_MS = 7 * 24 * 3600 * 1000;
const HIDE_PATHS = ["/login", "/root-admin", "/creator/login", "/mkt/auth", "/create", "/payment"];

export default function InstallAppBanner() {
  const [branding, setBranding] = useState(null);
  const [canPrompt, setCanPrompt] = useState(!!window.__pwaPrompt);
  const [showIOS, setShowIOS] = useState(false);
  const [gone, setGone] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app-pwa/branding`)
      .then((r) => r.json()).then(setBranding).catch(() => {});
    const onReady = () => setCanPrompt(true);
    window.addEventListener("pwa-can-install", onReady);
    return () => window.removeEventListener("pwa-can-install", onReady);
  }, []);

  const snoozed = (() => { try { return Number(localStorage.getItem(DISMISS_KEY) || 0) > Date.now(); } catch { return false; } })();
  const hiddenPath = HIDE_PATHS.some((p) => loc.pathname.startsWith(p));
  const visible = !!branding && branding.banner_enabled && !isStandalone() && !snoozed && !gone && !hiddenPath;

  useEffect(() => {
    if (visible) trackAppEvent("install_banner_view");
  }, [visible, loc.pathname]);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + SNOOZE_MS)); } catch { /* noop */ }
    setGone(true);
  };

  const install = async () => {
    trackAppEvent("install_button_click");
    const prompt = window.__pwaPrompt;
    if (prompt) {
      prompt.prompt();
      try {
        const { outcome } = await prompt.userChoice;
        trackAppEvent(outcome === "accepted" ? "pwa_install_accepted" : "pwa_install_dismissed");
        if (outcome === "accepted") { setGone(true); window.__pwaPrompt = null; setCanPrompt(false); }
      } catch { /* prompt failed — nothing claimed */ }
    } else {
      setShowIOS(true);
    }
  };

  return (
    <>
      <AnimatePresence>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
          className="fixed z-[70] inset-x-3 bottom-20 lg:inset-x-auto lg:right-6 lg:bottom-6 lg:w-[380px]"
          data-testid="pwa-install-banner">
          <div className="rounded-2xl p-4 shadow-2xl border border-emerald-400/20"
            style={{ background: "linear-gradient(135deg,#062A20,#07110E)", backdropFilter: "blur(16px)" }}>
            <button onClick={dismiss} data-testid="pwa-banner-dismiss" aria-label="Dismiss"
              className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full bg-white/10 text-white/70 flex items-center justify-center hover:bg-white/20 transition-colors">
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3.5 pr-6">
              <img src={branding.icon} alt={branding.app_name} className="h-12 w-12 rounded-xl shrink-0" />
              <div className="min-w-0">
                <p className="font-display font-extrabold text-white text-sm truncate">{branding.banner_title || `Install ${branding.app_name}`}</p>
                <p className="text-[12px] text-[#B8C9C1] leading-snug line-clamp-2">{branding.banner_text}</p>
              </div>
            </div>
            <button onClick={install} data-testid="pwa-install-btn"
              className="mt-3 w-full h-10 rounded-full font-bold text-[#04150e] inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-95"
              style={{ background: "linear-gradient(135deg,#00E676,#B7F34A)" }}>
              <Download className="h-4 w-4" /> Install App
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      {showIOS && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOS(false)} data-testid="pwa-ios-modal">
          <div className="rounded-2xl p-6 w-full max-w-sm border border-emerald-400/20" style={{ background: "#0a1f18" }}
            onClick={(e) => e.stopPropagation()}>
            <p className="font-display font-extrabold text-white text-lg mb-3">Install on this device</p>
            <ol className="text-sm text-[#B8C9C1] space-y-2.5 list-decimal list-inside">
              <li>Tap the <Share className="inline h-4 w-4 text-emerald-400 -mt-0.5" /> Share button in your browser</li>
              <li>Scroll down and tap <b className="text-white">"Add to Home Screen"</b></li>
              <li>Tap <b className="text-white">Add</b> — {branding.app_name} appears on your home screen</li>
            </ol>
            <button onClick={() => setShowIOS(false)} data-testid="pwa-ios-close"
              className="mt-5 w-full h-10 rounded-full font-bold text-[#04150e]" style={{ background: "#00E676" }}>Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
